/**
 * @file     SystemPage.tsx
 * @purpose  Shared frame for the 404 and error pages: an error-code eyebrow,
 *           the H1, one line, one next action (a button or a search field
 *           passed in), and a hairline matrix of crawlable popular pages
 *           (RelatedLinks), centred over the hero wash.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { HubActionButton, type HubAction } from '@/components/hub/HubActionButton';
import { RelatedLinks, type RelatedLinkItem } from '@/components/hub/RelatedLinks';
import { eyebrowSx, hubColors } from '@/components/hub/hubKitStyles';
import { hubTypographyTokens } from '@/design-system/tokens/typography';

export interface SystemPageProps {
  /** e.g. "Error 404". */
  eyebrow?: string;
  title: string;
  /** One line. */
  description?: string;
  /** The one next action as a button. */
  action?: HubAction;
  /** Or a custom control in its place, e.g. a search field. */
  actionSlot?: ReactNode;
  links?: RelatedLinkItem[];
  /** Micro label over the links, e.g. "Popular pages". */
  linksTitle?: string;
}

export function SystemPage({ eyebrow, title, description, action, actionSlot, links, linksTitle }: SystemPageProps) {
  return (
    <Box
      data-system-page
      sx={{
        background: `radial-gradient(ellipse 55% 60% at 50% 0%, ${hubColors.heroWash}, transparent 70%), ${hubColors.backgroundPrimary}`,
        py: { xs: 12, md: 20 },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3, maxWidth: 640, mx: 'auto' }}>
          {eyebrow ? (
            <Box component="p" sx={eyebrowSx}>
              {eyebrow}
            </Box>
          ) : null}
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: hubTypographyTokens.h1.fluid,
              fontWeight: hubTypographyTokens.h1.fontWeight,
              letterSpacing: hubTypographyTokens.h1.letterSpacing,
              lineHeight: hubTypographyTokens.h1.lineHeight,
              textWrap: 'balance',
              color: hubColors.textPrimary,
            }}
          >
            {title}
          </Box>
          {description ? (
            <Box component="p" sx={{ m: 0, fontSize: { xs: 17, md: 19 }, lineHeight: 1.5, color: hubColors.textSecondary }}>
              {description}
            </Box>
          ) : null}
          {actionSlot || action ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pt: 3 }}>
              {actionSlot ?? (action ? <HubActionButton action={action} /> : null)}
            </Box>
          ) : null}
        </Box>
        {links && links.length > 0 ? (
          <Box sx={{ maxWidth: 960, mx: 'auto', mt: { xs: 12, md: 16 }, textAlign: 'start' }}>
            <RelatedLinks items={links} title={linksTitle} titleVariant="label" />
          </Box>
        ) : null}
      </Container>
    </Box>
  );
}
